import React, { useState, useEffect } from 'react';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { Form, Button, Alert, Badge } from 'react-bootstrap';
import {
  FaEdit, FaTrash, FaSpinner, FaPlus, FaMoneyBillWave,
  FaCoins, FaUsers, FaSearch, FaHandHoldingUsd, FaCheckCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../context/AuthContext';
import { pledgeApi } from '../../api/pledges';
import { fundApi } from '../../api/funds';
import { eventApi } from '../../api/events';
import './Michango.css';
import { useDebounce } from '../../hooks/useDebounce';
import ExportUtility from '../../components/ExportUtility/ExportUtility';
import OngezaMchangoModal from '../../modals/michangoModal/OngezaMchangoModal';
import HaririMchangoModal from '../../modals/michangoModal/HaririMchangoModal';
import FutaMchangoModal from '../../modals/michangoModal/FutaMchangoModal';

const Michango = () => {
  const { currentEvent, isAuthenticated } = useAuthContext();

  // State for pledges data from /api/funds
  const [pledges, setPledges] = useState([]);
  const [stats, setStats] = useState({
    contributions: null,
    pledges: null,
    contributors: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination & Search UI State
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, 500);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  // Fetch funds directly from /api/funds and stats from /api/event-stats
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchFundsData();
    }
  }, [isAuthenticated, currentEvent]);

  const fetchFundsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [fundsRes, statsRes] = await Promise.allSettled([
        fundApi.getFunds(),
        eventApi.getEventStats()
      ]);

      if (statsRes.status === 'fulfilled') {
        const sData = statsRes.value?.data?.data || statsRes.value?.data || {};
        setStats({
          contributions: sData.contributions !== undefined && sData.contributions !== null ? parseFloat(sData.contributions) : null,
          pledges: sData.pledges !== undefined && sData.pledges !== null ? parseFloat(sData.pledges) : null,
          contributors: sData.contributors !== undefined && sData.contributors !== null ? parseInt(sData.contributors, 10) : (sData.pledgers !== undefined && sData.pledgers !== null ? parseInt(sData.pledgers, 10) : null)
        });
      }

      const response = fundsRes.status === 'fulfilled' ? fundsRes.value : null;
      if (!response && fundsRes.status === 'rejected') {
        throw fundsRes.reason;
      }
      const resData = response?.data;

      let fundsList = [];
      if (Array.isArray(resData)) {
        fundsList = resData;
      } else if (Array.isArray(resData?.data?.data)) {
        fundsList = resData.data.data;
      } else if (Array.isArray(resData?.data)) {
        fundsList = resData.data;
      }

      const formattedFunds = fundsList.map((item, idx) => {
        const fId = item.id || (idx + 1);
        const name = item.pledger || item.pledge?.guest?.name || item.full_name || 'Mgeni';
        const phone = item.pledge?.guest?.phone_number || item.phone_number || item.mobile || '-';
        const paidAmt = parseFloat(item.amount || 0);
        const pledgeAmt = parseFloat(item.pledge?.amount || item.pledge_amount || 0);
        const remaining = Math.max(0, pledgeAmt - paidAmt);

        let dateStr = '-';
        if (item.created_at) {
          try {
            const d = new Date(item.created_at);
            if (!isNaN(d.getTime())) {
              dateStr = d.toLocaleDateString('sw-TZ', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            } else {
              dateStr = String(item.created_at).split('T')[0];
            }
          } catch (e) {
            dateStr = String(item.created_at).split('T')[0];
          }
        }

        let groupsArr = [];
        const rawGroups = item.pledge?.guest?.groups || item.groups || [];
        if (Array.isArray(rawGroups) && rawGroups.length > 0) {
          groupsArr = rawGroups.map(g => (typeof g === 'object' ? (g.group_name || g.name) : g)).filter(Boolean);
        }

        const recordedBy = item.recorded_by || item.recorder?.full_name || item.received_by || 'Event Admin';

        return {
          id: fId,
          pledge_id: item.pledge_id,
          full_name: name,
          phone: phone,
          pledge_amount: pledgeAmt,
          paid_amount: paidAmt,
          balance: remaining,
          amount_raw: item.amount,
          description: item.description || '-',
          received_by: item.received_by || recordedBy,
          recorded_by: recordedBy,
          created_at_formatted: dateStr,
          groups: groupsArr,
          groups_str: groupsArr.length > 0 ? groupsArr.join(', ') : '-',
          amount: `TZS ${paidAmt.toLocaleString()}`,
          pledge_formatted: pledgeAmt > 0 ? `TZS ${pledgeAmt.toLocaleString()}` : '-',
          balance_formatted: remaining > 0 ? `TZS ${remaining.toLocaleString()}` : 'TZS 0',
          status: remaining === 0 && pledgeAmt > 0 ? 'Imekamilika' : (paidAmt > 0 ? 'Inaendelea' : 'Haijaanza')
        };
      });

      setPledges(formattedFunds);
    } catch (err) {
      console.error('Error fetching funds from /api/funds:', err);
      setPledges([]);
      setError('Imefeli kupakia michango (/api/funds).');
      toast.error('Failed to load funds');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: '#',
      cell: (row, idx) => idx + 1,
      width: '55px',
      center: true
    },
    {
      name: 'Mchangiaji',
      selector: (row) => row.full_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div>
          <div className="fw-bold text-dark">{row.full_name}</div>
          <small className="text-muted">{row.phone}</small>
        </div>
      )
    },
    {
      name: 'Ahadi (TZS)',
      selector: (row) => row.pledge_amount,
      sortable: true,
      cell: (row) => <span className="fw-semibold text-primary">{row.pledge_formatted}</span>
    },
    {
      name: 'Kiasi (TZS)',
      selector: (row) => row.paid_amount,
      sortable: true,
      cell: (row) => <span className="fw-bold text-success">{row.amount}</span>
    },
    {
      name: 'Aliyerekodi',
      selector: (row) => row.recorded_by,
      sortable: true,
      cell: (row) => <span className="text-dark small">{row.recorded_by}</span>
    },
    {
      name: 'Tarehe',
      selector: (row) => row.created_at_formatted,
      sortable: true,
      cell: (row) => <small className="text-muted">{row.created_at_formatted}</small>
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="action-buttons d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={true}
            className="p-1 rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: '30px', height: '30px', opacity: 0.5, cursor: 'not-allowed' }}
            title="Haiongozwi hariri"
          >
            <FaEdit size={13} />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="p-1 rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: '30px', height: '30px' }}
            onClick={() => {
              setSelectedContribution(row);
              setShowDeleteModal(true);
            }}
            title="Futa Mchango"
          >
            <FaTrash size={13} />
          </Button>
        </div>
      ),
      width: '100px',
      center: true
    },
  ];

  const filteredData = pledges.filter(item =>
    String(item.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.phone || '').includes(searchTerm) ||
    String(item.recorded_by || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.amount_raw || '').includes(searchTerm)
  );

  // Summary statistics using /api/event-stats with fallback
  const totalPaid = stats.contributions !== null && !isNaN(stats.contributions)
    ? stats.contributions
    : pledges.reduce((sum, item) => sum + (item.paid_amount || 0), 0);

  const totalPledge = stats.pledges !== null && !isNaN(stats.pledges)
    ? stats.pledges
    : pledges.reduce((sum, item) => sum + (item.pledge_amount || 0), 0);

  const totalBalance = Math.max(0, totalPledge - totalPaid);

  const totalContributors = stats.contributors !== null && !isNaN(stats.contributors)
    ? stats.contributors
    : pledges.length;

  // Export Columns definition for Funds Export (/export/funds/json)
  const exportCols = [
    { header: 'Mchangiaji', dataKey: 'full_name' },
    { header: 'Kiasi (TZS)', dataKey: 'amount_formatted' },
    { header: 'Aliyerekodi', dataKey: 'recorded_by' },
    { header: 'Tarehe', dataKey: 'created_at_formatted' }
  ];

  const fetchExportData = async () => {
    try {
      const response = await fundApi.exportFundsJson();
      const resData = response?.data;
      let fundsList = [];
      if (Array.isArray(resData)) fundsList = resData;
      else if (Array.isArray(resData?.data)) fundsList = resData.data;

      return fundsList.map(item => {
        const amt = parseFloat(item.amount || 0);
        let dateStr = '-';
        if (item.created_at) {
          try {
            const d = new Date(item.created_at);
            if (!isNaN(d.getTime())) {
              dateStr = d.toLocaleDateString('sw-TZ', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            } else {
              dateStr = String(item.created_at).split('T')[0];
            }
          } catch (e) {
            dateStr = String(item.created_at).split('T')[0];
          }
        }

        return {
          id: item.id,
          full_name: item.full_name || 'Mgeni',
          amount_formatted: amt.toLocaleString(),
          recorded_by: item.recorded_by || 'Event Admin',
          created_at_formatted: dateStr
        };
      });
    } catch (e) {
      console.error('Error fetching export funds JSON:', e);
      return pledges.map(item => ({
        full_name: item.full_name,
        amount_formatted: item.paid_amount.toLocaleString(),
        recorded_by: 'Event Admin',
        created_at_formatted: '-'
      }));
    }
  };

  // Custom empty state component
  const EmptyState = () => (
    <div className="empty-state-container">
      <div className="empty-state-icon">
        <FaMoneyBillWave size={64} className="text-muted" />
      </div>
      <h5 className="empty-state-title">Hakuna Michango</h5>
      <p className="empty-state-message">
        Bado hakuna mchango uliorekodiwa. Bonyeza kitufe cha "Ongeza Mchango" kuanza.
      </p>
      <Button
        variant="primary"
        onClick={() => setShowModal(true)}
        className="empty-state-btn"
      >
        <FaPlus className="me-2" />
        Rekodi Mchango wa Kwanza
      </Button>
    </div>
  );

  if (loading && pledges.length === 0) {
    return (
      <div className="michango-loading">
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3">Inapakia michango...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="michango-container py-4 px-2 px-md-4">
      <div className="container-fluid">
        {/* Title & Subtitle Header */}
        <div className="text-center mb-4">
          <h1 className="stylish-heading mb-1" style={{ color: '#4169e1' }}>
            Usimamizi wa Michango
          </h1>
          <p className="text-muted small mb-0">
            Usimamizi na uratibu wa michango na miamala ya shughuli.
          </p>
        </div>

        {/* Executive Stats Summary Row */}
        <div className="row g-2 g-md-3 mb-4 align-items-stretch">
          <div className="col-6 col-md-3">
            <div className="bg-white p-2 p-md-3 rounded-4 shadow-sm border d-flex align-items-center gap-2 h-100">
              <div className="p-2 p-md-3 rounded-3 bg-success-subtle text-success d-flex align-items-center justify-content-center flex-shrink-0">
                <FaMoneyBillWave size={20} />
              </div>
              <div className="overflow-hidden">
                <span className="text-muted d-block fw-medium text-truncate" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Jumla ya Michango</span>
                <span className="fw-bold fs-6 fs-md-5 text-success">TZS {totalPaid.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="bg-white p-2 p-md-3 rounded-4 shadow-sm border d-flex align-items-center gap-2 h-100">
              <div className="p-2 p-md-3 rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0">
                <FaCoins size={20} />
              </div>
              <div className="overflow-hidden">
                <span className="text-muted d-block fw-medium text-truncate" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Jumla ya Ahadi</span>
                <span className="fw-bold fs-6 fs-md-5 text-primary">TZS {totalPledge.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="bg-white p-2 p-md-3 rounded-4 shadow-sm border d-flex align-items-center gap-2 h-100">
              <div className="p-2 p-md-3 rounded-3 bg-danger-subtle text-danger d-flex align-items-center justify-content-center flex-shrink-0">
                <FaHandHoldingUsd size={20} />
              </div>
              <div className="overflow-hidden">
                <span className="text-muted d-block fw-medium text-truncate" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Jumla ya Salio (Baki)</span>
                <span className="fw-bold fs-6 fs-md-5 text-danger">TZS {totalBalance.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="bg-white p-2 p-md-3 rounded-4 shadow-sm border d-flex align-items-center gap-2 h-100">
              <div className="p-2 p-md-3 rounded-3 bg-warning-subtle text-warning d-flex align-items-center justify-content-center flex-shrink-0">
                <FaUsers size={20} />
              </div>
              <div className="overflow-hidden">
                <span className="text-muted d-block fw-medium text-truncate" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Idadi Ya WalioChangia</span>
                <span className="fw-bold fs-6 fs-md-5 text-dark">{totalContributors}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Unified Search & Action Toolbar */}
        <div className="bg-white p-3 rounded-4 shadow-sm border mb-4 d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3">
          <div className="position-relative flex-grow-1" style={{ maxWidth: '420px' }}>
            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 5 }} />
            <Form.Control
              type="text"
              placeholder="Tafuta kwa jina la mchangiaji, simu, au aliyerekodi..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="ps-5 shadow-none border"
              style={{ borderRadius: '25px', height: '42px', fontSize: '0.85rem', borderColor: '#e2e8f0' }}
            />
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap flex-sm-nowrap">
            <Button
              variant="primary"
              onClick={() => setShowModal(true)}
              className="rounded-pill px-3 px-md-4 py-2 fw-semibold shadow-sm border-0 d-flex align-items-center justify-content-center gap-2 flex-grow-1 flex-sm-grow-0"
              style={{ backgroundColor: '#4169e1', minHeight: '42px' }}
            >
              <FaPlus size={15} />
              Ongeza Mchango
            </Button>

            <ExportUtility
              fetchData={fetchExportData}
              columns={exportCols}
              filename={`Ripoti_ya_Michango_${currentEvent?.event_title || 'Harusi'}`}
              excelOnlyButton={true}
              excelButtonText="Excel"
              btnClassName="btn btn-outline-success rounded-pill px-3 py-2 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2 flex-grow-1 flex-sm-grow-0"
              btnStyle={{ minHeight: '42px', borderRadius: '25px' }}
            />

            <ExportUtility
              fetchData={fetchExportData}
              columns={exportCols}
              filename={`Ripoti_ya_Michango_${currentEvent?.event_title || 'Harusi'}`}
              pdfTitle={`Ripoti ya Michango na Mapato - ${currentEvent?.event_title || ''}`}
              pdfOnlyButton={true}
              pdfButtonText="PDF"
              btnClassName="btn btn-outline-danger rounded-pill px-3 py-2 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2 flex-grow-1 flex-sm-grow-0"
              btnStyle={{ minHeight: '42px', borderRadius: '25px' }}
            />
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="mt-3" onClose={() => setError(null)} dismissible>
            <Alert.Heading>Hitilafu!</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" size="sm" onClick={fetchFundsData}>
              Jaribu Tena
            </Button>
          </Alert>
        )}

        {/* Data Table */}
        <div className="michango-table-wrapper">
          {pledges.length > 0 ? (
            <StyledDataTable
              columns={columns}
              data={filteredData}
              pagination={false}
              progressPending={loading}
              emptyMessage="Hakuna matokeo yanayolingana na utafutaji wako."
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>

      {/* External Modals */}
      <OngezaMchangoModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSuccess={fetchFundsData}
      />

      <HaririMchangoModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedContribution(null);
        }}
        contribution={selectedContribution}
        onSuccess={fetchFundsData}
      />

      <FutaMchangoModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedContribution(null);
        }}
        contribution={selectedContribution}
        onSuccess={fetchFundsData}
      />
    </div>
  );
};

export default Michango;